import React, { useState } from 'react';
import { Button, Card, Col, Placeholder, Row } from 'react-bootstrap';
import { LAYER_ORDER, MAP_LAYERS } from './satelliteModel';
import { classLabel, renderSatText, satDate, t, toneColour, toneWord } from './satelliteText';

const formatPct = (pct) => (pct > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`);

/**
 * One layer's card: CropGen's classes over the field, each with its share and
 * how our rules read it; the layer's line about the field; and, on request,
 * the numbers the answer carried. (farmer-app StageWatchList LayerMetric)
 */
function LayerMetric({ id, entry, signal, raw }) {
  const layer = MAP_LAYERS[id];
  const loading = !entry || entry.status === 'loading';
  const classified = entry && entry.classified;
  const data = entry && entry.status === 'ready' ? entry.data : null;
  const classes = classified ? classified.classes.filter((c) => c.percent > 0) : [];

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="mb-2">
          <span className="fw-bold">{t(layer.labelKey)}</span>{' '}
          <small className="badge text-bg-light border">{layer.code}</small>
          <small className="d-block text-muted">{t(`mfWhat_${id}`)}</small>
        </div>

        {loading && (
          <Placeholder as="div" animation="glow">
            <Placeholder xs={12} />
          </Placeholder>
        )}
        {!loading &&
          classes.map((c) => (
            <div key={c.label} className="d-flex align-items-center gap-2 small mb-1">
              <span style={{ width: 110 }} className="text-truncate" title={classLabel(c.label)}>
                {classLabel(c.label)}
              </span>
              <span className="flex-grow-1 bg-light rounded" style={{ height: 8 }}>
                <i className="d-block rounded" style={{ height: 8, width: `${Math.max(2, c.percent)}%`, background: c.color }} />
              </span>
              <span className="text-end text-nowrap" style={{ minWidth: 96 }}>
                <strong>{formatPct(c.percent)}</strong>{' '}
                <small className="fw-bold" style={{ color: toneColour(c.tone) }}>
                  {toneWord(c.tone).toLocaleUpperCase()}
                </small>
              </span>
            </div>
          ))}
        {!loading && signal && <p className="small mt-2 mb-0">{renderSatText(signal.plain)}</p>}

        {raw && data && (
          <pre className="small bg-light rounded p-2 mt-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
            {`${layer.code} · ${satDate(data.date)}`}
            {classified ? `\n${t('mfRawClear', { clear: Math.round(classified.clearPct), cloud: Math.round(classified.cloudPct) })}` : ''}
            {(data.legend || [])
              .filter((e) => Number(e.percent) > 0)
              .map((e) => `\n${classLabel(e.label)} · ${Number(e.percent).toFixed(1)}% · ${Number(e.hectares || 0).toFixed(3)} ha`)
              .join('')}
          </pre>
        )}
      </Card.Body>
    </Card>
  );
}

/**
 * "What the satellite measures": one card per layer, the no-stage mode of the
 * farmer app's "What we watch at this stage".
 */
export default function SatelliteMeasuresSection({ entries, signals }) {
  const [raw, setRaw] = useState(false);
  return (
    <>
      <Row xs={1} md={2} xxl={4} className="g-3">
        {LAYER_ORDER.map((id) => (
          <Col key={id}>
            <LayerMetric id={id} entry={entries[id]} signal={signals[id]} raw={raw} />
          </Col>
        ))}
      </Row>
      <Button variant="link" className="px-0 mt-2" onClick={() => setRaw((v) => !v)} aria-expanded={raw}>
        {t(raw ? 'mfHideNumbers' : 'mfShowNumbers')}
      </Button>
    </>
  );
}
